import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { ItemData } from 'src/app/features/catalogs/components/inserting-locations/inserting-locations.component';
import {
  ICatalogFilter,
  ICatalogFilterRequest,
} from 'src/app/features/catalogs/models/catalogs.models';
import {
  IGroupFilter,
  IGroupFilterRequest,
} from 'src/app/features/companies-groups/models/companies-groups.model';
import {
  ICompanyFilter,
  ICompanyFilterRequest,
  ILocation,
} from 'src/app/features/customers/models/customers.models';
import {
  IImportFilter,
  IImportFilterRequest,
} from 'src/app/features/imports/models/imports.models';
import {
  IOrderFilterRequest,
  IOrdersFilter,
} from 'src/app/features/orders/models/order.model';
import {
  IInventoryFilter,
  IInventoryFilterRequest,
} from 'src/app/features/produits/models/products.models';
import { OrderPaymentStatusEnum } from 'src/app/features/quotations/enum/quotations.status.enum';
import {
  IQuotationFilter,
  IQuotationFilterRequest,
} from 'src/app/features/quotations/models/quotations.models';
import {
  ISegmentFilter,
  ISegmentFilterRequest,
} from 'src/app/features/segments/models/segment.model';
import { SetNavbarParams } from '../store/shared.actions';
import { ActivatedRoute } from '@angular/router';
import { IAgentFilter, IAgentFilterRequest } from 'src/app/features/agents/models/agents.models';

export const parseInventoryFilterRequest = (
  filter: IInventoryFilter
): IInventoryFilterRequest => {
  const filters: { key: string; operator: string; value: any }[] = [];

  const productsInfoFilter = filter?.productsInfoFilter;
  if (productsInfoFilter?.name)
    filters.push({
      key: 'name',
      operator: 'contains',
      value: productsInfoFilter?.name,
    });
  if (productsInfoFilter?.sku)
    filters.push({
      key: 'sku',
      operator: 'contains',
      value: productsInfoFilter?.sku,
    });
    if (productsInfoFilter?.barcode != null)
    filters.push({
      key: 'barcode',
      operator: 'contains',
      value: productsInfoFilter?.barcode,
    })
  if (productsInfoFilter?.status !== undefined)
    filters.push({
      key: 'status',
      operator: 'equals',
      value: productsInfoFilter?.status,
    });
  if (productsInfoFilter?.brands?.length > 0)
    filters.push({
      key: 'brand',
      operator: 'in',
      value: productsInfoFilter?.brands.map((brand) => brand.id),
    });
  if (productsInfoFilter?.has_variants !== undefined)
    filters.push({
      key: 'has_variants',
      operator: 'equals',
      value: productsInfoFilter?.has_variants === 'true',
    });

  const inventoryFilter = filter?.inventoryFilter;
  if (inventoryFilter?.cost_min !== undefined)
    filters.push({
      key: 'cost',
      operator: 'greater_than_or_equals',
      value: inventoryFilter?.cost_min,
    });
  if (inventoryFilter?.cost_max !== undefined)
    filters.push({
      key: 'cost',
      operator: 'less_than_or_equals',
      value: inventoryFilter?.cost_max,
    });
  if (inventoryFilter?.sales_price_min !== undefined)
    filters.push({
      key: 'sales_price',
      operator: 'greater_than_or_equals',
      value: inventoryFilter?.sales_price_min,
    });
  if (inventoryFilter?.sales_price_max !== undefined)
    filters.push({
      key: 'sales_price',
      operator: 'less_than_or_equals',
      value: inventoryFilter?.sales_price_max,
    });
  if (inventoryFilter?.quantity_min !== undefined)
    filters.push({
      key: 'quantity',
      operator: 'greater_than_or_equals',
      value: inventoryFilter?.quantity_min,
    });
  if (inventoryFilter?.quantity_max !== undefined)
    filters.push({
      key: 'quantity',
      operator: 'less_than_or_equals',
      value: inventoryFilter?.quantity_max,
    });
  if (inventoryFilter?.reorder_point_min !== undefined)
    filters.push({
      key: 'reorder_point',
      operator: 'greater_than_or_equals',
      value: inventoryFilter?.reorder_point_min,
    });
  if (inventoryFilter?.reorder_point_max !== undefined)
    filters.push({
      key: 'reorder_point',
      operator: 'less_than_or_equals',
      value: inventoryFilter?.reorder_point_max,
    });
  if (inventoryFilter?.expiry_date_min !== undefined && isValidDate(inventoryFilter?.expiry_date_min))
    filters.push({
      key: 'expiry_date',
      operator: 'greater_than_or_equals',
      value: inventoryFilter?.expiry_date_min,
    });
  if (inventoryFilter?.expiry_date_max !== undefined && isValidDate(inventoryFilter?.expiry_date_max))
    filters.push({
      key: 'expiry_date',
      operator: 'less_than_or_equals',
      value: inventoryFilter?.expiry_date_max,
    });

  const organizeFilter = filter?.organizeFilter;
  if (organizeFilter?.categories?.length > 0)
    filters.push({
      key: 'category',
      operator: 'in',
      value: organizeFilter?.categories.map((category) => category.id),
    });
  if (organizeFilter?.tags?.length > 0)
    filters.push({
      key: 'tag',
      operator: 'in',
      value: organizeFilter?.tags.map((tag) => tag.id),
    });
  if (organizeFilter?.has_catalogs !== undefined)
    filters.push({
      key: 'has_catalogs',
      operator: 'equals',
      value: organizeFilter?.has_catalogs === 'true',
    });
  if (organizeFilter?.number_of_catalogs !== undefined)
    filters.push({
      key: 'number_of_catalogs',
      operator: 'equals',
      value: organizeFilter?.number_of_catalogs,
    });

  return { filters };
};

export const refillInventoryFilter = (
  filter?: IInventoryFilter
): IInventoryFilter => {
  const refillFilter: IInventoryFilter = {
    productsInfoFilter: {
      name: filter?.productsInfoFilter?.name,
      sku: filter?.productsInfoFilter?.sku,
      barcode: filter?.productsInfoFilter?.barcode,
      brands: filter?.productsInfoFilter?.brands || [],
      status: filter?.productsInfoFilter?.status,
      has_variants: filter?.productsInfoFilter?.has_variants,
    },
    inventoryFilter: {
      sales_price_min: filter?.inventoryFilter?.sales_price_min,
      sales_price_max: filter?.inventoryFilter?.sales_price_max,
      cost_min: filter?.inventoryFilter?.cost_min,
      cost_max: filter?.inventoryFilter?.cost_max,
      quantity_min: filter?.inventoryFilter?.quantity_min,
      quantity_max: filter?.inventoryFilter?.quantity_max,
      reorder_point_min: filter?.inventoryFilter?.reorder_point_min,
      reorder_point_max: filter?.inventoryFilter?.reorder_point_max,
      expiry_date_min: isValidDate(filter?.inventoryFilter?.expiry_date_min) ? filter?.inventoryFilter?.expiry_date_min : undefined,
      expiry_date_max: isValidDate(filter?.inventoryFilter?.expiry_date_max) ? filter?.inventoryFilter?.expiry_date_max : undefined,
    },
    organizeFilter: {
      categories: filter?.organizeFilter?.categories || [],
      tags: filter?.organizeFilter?.tags || [],
      has_catalogs: filter?.organizeFilter?.has_catalogs,
      number_of_catalogs: filter?.organizeFilter?.number_of_catalogs,
    },
  };

  return JSON.parse(JSON.stringify(refillFilter));
};

export const parseQuotationFilterRequest = (
  filter: IQuotationFilter
): IQuotationFilterRequest => {
  const filters: { key: string; operator: string; value: any }[] = [];

  const quotationInfoFilter = filter?.quotationInfoFilter;
  if (quotationInfoFilter?.id_order !== undefined)
    filters.push({
      key: 'name',
      operator: 'contains',
      value: quotationInfoFilter?.id_order,
    });
  if (quotationInfoFilter?.sales_person !== undefined)
    filters.push({
      key: 'sales_person',
      operator: 'contains',
      value: quotationInfoFilter?.sales_person,
    });
  if (quotationInfoFilter?.status !== undefined)
    filters.push({
      key: 'status',
      operator: 'equals',
      value: quotationInfoFilter?.status,
    });
  if (quotationInfoFilter?.channel !== undefined)
    filters.push({
      key: 'channel',
      operator: 'equals',
      value: quotationInfoFilter?.channel,
    });
  if (quotationInfoFilter?.external_id !== undefined)
    filters.push({
      key: 'external_id',
      operator: 'contains',
      value: quotationInfoFilter?.external_id,
    });
  if (quotationInfoFilter?.order_id !== undefined)
    filters.push({
      key: 'order_id',
      operator: 'contains',
      value: quotationInfoFilter?.order_id,
    });
  if (quotationInfoFilter?.buyer_po !== undefined)
    filters.push({
      key: 'buyer_po',
      operator: 'contains',
      value: quotationInfoFilter?.buyer_po,
    });
  if (quotationInfoFilter?.from_date !== undefined)
    filters.push({
      key: 'date_order',
      operator: 'greater_than_or_equals',
      value: quotationInfoFilter?.from_date,
    });
  if (quotationInfoFilter?.to_date !== undefined)
    filters.push({
      key: 'date_order',
      operator: 'less_than_or_equals',
      value: quotationInfoFilter?.to_date,
    });
  if (quotationInfoFilter?.latest_event_date !== undefined)
    filters.push({
      key: 'latest_event_date',
      operator: 'less_than_or_equals',
      value: quotationInfoFilter?.latest_event_date,
    });
  if (quotationInfoFilter?.products && quotationInfoFilter.products.length > 0) {
    const skus = quotationInfoFilter.products.map((p) => p.sku).filter(Boolean);
    if (skus.length > 0)
      filters.push({
        key: 'product_sku',
        operator: 'in',
        value: skus,
      });
  }
  // Truthy (not strict === true): the URL round-trip encodes booleans as 1/0
  // (deepConvertBooleans), so has_duplicates comes back as 1, not true.
  if (quotationInfoFilter?.has_duplicates)
    filters.push({ key: 'has_duplicates', operator: 'equals', value: true });

  const extraInfoFilter = filter?.extraInfoFilter;
  if (extraInfoFilter?.total_sku_min !== undefined)
    filters.push({
      key: 'nbr_products',
      operator: 'greater_than_or_equals',
      value: extraInfoFilter?.total_sku_min,
    });
  if (extraInfoFilter?.total_sku_max !== undefined)
    filters.push({
      key: 'nbr_products',
      operator: 'less_than_or_equals',
      value: extraInfoFilter?.total_sku_max,
    });
  if (extraInfoFilter?.total_price_min !== undefined)
    filters.push({
      key: 'amount_total',
      operator: 'greater_than_or_equals',
      value: extraInfoFilter?.total_price_min,
    });
  if (extraInfoFilter?.total_price_max !== undefined)
    filters.push({
      key: 'amount_total',
      operator: 'less_than_or_equals',
      value: extraInfoFilter?.total_price_max,
    });

  const organizeFilter = filter?.organizeFilter;
  if (organizeFilter?.catalogs?.length > 0)
    filters.push({
      key: 'pricelist',
      operator: 'in',
      value: organizeFilter?.catalogs.map((catalog) => catalog.id),
    });
  // `location` is the backend's own key for sale.order.partner_id, so the union
  // of a company's location ids is how "that company's orders" is expressed.
  if ((organizeFilter?.locations?.length ?? 0) > 0)
    filters.push({
      key: 'location',
      operator: 'in',
      value: organizeFilter!.locations!.map((location) => location.id),
    });

  const own = filter?.ownershipFilter;
  if (own?.my_orders === true) {
    filters.push({ key: 'my_orders', operator: 'equals', value: true });
  }
  const ownerId = own?.owner?.id;
  if (ownerId !== undefined && ownerId !== null) {
    filters.push({
      key: 'owner_id',
      operator: 'equals',
      value: ownerId,
    });
  }
  const creatorId = own?.creator?.id;
  if (creatorId !== undefined && creatorId !== null) {
    filters.push({
      key: 'created_by_id',
      operator: 'equals',
      value: creatorId,
    });
  }
  // `team_ids` is the backend's own key: it expands to "owned by any member of ANY of
  // these teams". One criterion for the whole selection, never one per team -- a list
  // value is existing precedent in that builder (see `product_sku`).
  //
  // Emitted only when at least one team is picked. A present-but-empty `team_ids` would
  // be a FALSE leaf on the backend and return zero orders rather than all of them: the
  // guard is on the SELECTION, and the resolution side deliberately has none (D-02).
  const teamIds = (own?.teams ?? [])
    .map((team) => team.id)
    .filter((id) => id !== undefined && id !== null);
  if (teamIds.length) {
    filters.push({
      key: 'team_ids',
      operator: 'in',
      value: teamIds,
    });
  }

  return { filters };
};

export const refillQuotationFilter = (
  filter?: IQuotationFilter
): IQuotationFilter => {
  const ownLegacy = filter?.ownershipFilter as
    | IQuotationFilter['ownershipFilter']
    | { owner_id?: number; created_by_id?: number }
    | undefined;
  let owner = filter?.ownershipFilter?.owner;
  let creator = filter?.ownershipFilter?.creator;
  // The pre-multiselect single-team shape. An Orders URL bookmarked before WB-7898
  // encodes `ownershipFilter.team`, and without this the whitelist below drops it and the
  // bookmark silently returns the unfiltered list (D-03). Removable in a later release,
  // once no bookmark in the wild can still carry the scalar.
  const legacyTeam = (filter?.ownershipFilter as { team?: { id: number; name: string } })
    ?.team;
  // "explicitly cleared" and "never set" must not collapse into one branch -- see the
  // `teams` entry in the ownership whitelist below.
  const hasOwnTeams = !!filter?.ownershipFilter && 'teams' in filter.ownershipFilter;
  if (!owner && ownLegacy && 'owner_id' in ownLegacy && ownLegacy.owner_id != null) {
    owner = { id: ownLegacy.owner_id, name: '', email: undefined };
  }
  if (!creator && ownLegacy && 'created_by_id' in ownLegacy && ownLegacy.created_by_id != null) {
    creator = { id: ownLegacy.created_by_id, name: '', email: undefined };
  }
  const refillFilter: IQuotationFilter = {
    quotationInfoFilter: {
      id_order: filter?.quotationInfoFilter?.id_order,
      sales_person: filter?.quotationInfoFilter?.sales_person,
      status: filter?.quotationInfoFilter?.status,
      channel: filter?.quotationInfoFilter?.channel,
      external_id: filter?.quotationInfoFilter?.external_id,
      order_id: filter?.quotationInfoFilter?.order_id,
      buyer_po: filter?.quotationInfoFilter?.buyer_po,
      from_date: filter?.quotationInfoFilter?.from_date,
      to_date: filter?.quotationInfoFilter?.to_date,
      latest_event_date: filter?.quotationInfoFilter?.latest_event_date,
      products: filter?.quotationInfoFilter?.products || [],
      // Rehydrated like every other filter so it stays visible on the Filters
      // button (active state + clear) and survives URL/store round-trips.
      has_duplicates: filter?.quotationInfoFilter?.has_duplicates,
    },
    extraInfoFilter: {
      total_sku_min: filter?.extraInfoFilter?.total_sku_min,
      total_sku_max: filter?.extraInfoFilter?.total_sku_max,
      total_price_min: filter?.extraInfoFilter?.total_price_min,
      total_price_max: filter?.extraInfoFilter?.total_price_max,
    },
    organizeFilter: {
      catalogs: filter?.organizeFilter?.catalogs || [],
      // Preserved so a company-scoped filter injected from the company page survives the
      // URL -> refill -> parse round-trip, stays clearable, and is not silently dropped.
      locations: filter?.organizeFilter?.locations || [],
    },
    ownershipFilter: {
      my_orders: filter?.ownershipFilter?.my_orders,
      owner: owner ?? undefined,
      creator: creator ?? undefined,
      // This literal is a whitelist: a field absent from it is dropped on every URL
      // round-trip even though the interface and the parser both know about it. That was
      // WB-7502 for `locations`; `teams` is named here so it cannot repeat.
      //
      // `undefined` when empty, never `[]`: the JSON round-trip below strips undefined
      // keys but KEEPS an empty array, and an extra own key breaks the pre-existing
      // `resets every criterion` toEqual(refillQuotationFilter()).
      //
      // The legacy fallback is reached only when `teams` is ABSENT, never when it is
      // present and empty. `.length` alone conflates the two, so an ownershipFilter
      // carrying both `teams: []` and a lingering scalar `team` would resurrect the
      // selection the user just cleared. The app's own encoder cannot currently emit
      // that pair -- `removeEmptyProperties` maps `[]` to undefined and drops the key
      // before it ever reaches the URL -- but a hand-edited or hand-shared link can,
      // and nothing here should depend on an unrelated helper's pruning to stay correct.
      teams: hasOwnTeams
        ? filter!.ownershipFilter!.teams?.length
          ? filter!.ownershipFilter!.teams
          : undefined
        : legacyTeam
          ? [legacyTeam]
          : undefined,
    },
  };

  return JSON.parse(JSON.stringify(refillFilter));
};

export const parseCompanyFilterRequest = (
  filter: ICompanyFilter
): ICompanyFilterRequest => {
  const filters: { key: string; operator: string; value: any }[] = [];
  const companiesInfoFilter = filter?.companiesInfoFilter;
  if (companiesInfoFilter?.name)
    filters.push({
      key: 'name',
      operator: 'contains',
      value: companiesInfoFilter?.name,
    });
  if (companiesInfoFilter?.nbr_locations_min)
    filters.push({
      key: 'nbr_locations',
      operator: 'greater_than_or_equals',
      value: companiesInfoFilter?.nbr_locations_min,
    });
  if (companiesInfoFilter?.nbr_locations_max)
    filters.push({
      key: 'nbr_locations',
      operator: 'less_than_or_equals',
      value: companiesInfoFilter?.nbr_locations_max,
    });
  if (companiesInfoFilter?.total_sales_min)
    filters.push({
      key: 'total_sales',
      operator: 'greater_than_or_equals',
      value: companiesInfoFilter?.total_sales_min,
    });
  if (companiesInfoFilter?.total_sales_max)
    filters.push({
      key: 'total_sales',
      operator: 'less_than_or_equals',
      value: companiesInfoFilter?.total_sales_max,
    });
  if (companiesInfoFilter?.total_orders_min)
    filters.push({
      key: 'total_orders',
      operator: 'greater_than_or_equals',
      value: companiesInfoFilter?.total_orders_min,
    });
  if (companiesInfoFilter?.total_orders_max)
    filters.push({
      key: 'total_orders',
      operator: 'less_than_or_equals',
      value: companiesInfoFilter?.total_orders_max,
    });
  if (companiesInfoFilter?.active_from)
    filters.push({
      key: 'not_active_since',
      operator: 'greater_than_or_equals',
      value: companiesInfoFilter?.active_from,
    });
  if (companiesInfoFilter?.active_to)
    filters.push({
      key: 'not_active_since',
      operator: 'less_than_or_equals',
      value: companiesInfoFilter?.active_to,
    });
  const locationInfoFilter = filter?.locationInfoFilter;
  if (locationInfoFilter?.location_name)
    filters.push({
      key: 'location_name',
      operator: 'contains',
      value: locationInfoFilter?.location_name,
    });
  if (locationInfoFilter?.has_billing != null)
    filters.push({
      key: 'has_billing',
      operator: 'equals',
      value: locationInfoFilter?.has_billing === true,
    });
  if (locationInfoFilter?.shipping_term != null)
    filters.push({
      key: 'shipping_term',
      operator: 'equals',
      value: locationInfoFilter?.shipping_term === true,
    });
  if (locationInfoFilter?.payment_terms.length > 0)
    filters.push({
      key: 'payment_terms',
      operator: 'in',
      value: locationInfoFilter?.payment_terms?.map(
        (payment_term) => payment_term.id
      ),
    });

  const contactFilter = filter?.contactFilter;
  if (contactFilter?.contact_name)
    filters.push({
      key: 'contact_name',
      operator: 'contains',
      value: contactFilter?.contact_name,
    });
  if (contactFilter?.nbr_contacts_min)
    filters.push({
      key: 'nbr_contacts',
      operator: 'greater_than_or_equals',
      value: contactFilter?.nbr_contacts_min,
    });
  if (contactFilter?.nbr_contacts_max)
    filters.push({
      key: 'nbr_contacts',
      operator: 'less_than_or_equals',
      value: contactFilter?.nbr_contacts_max,
    });
  if (contactFilter?.contacts_access_to_mktp_min)
    filters.push({
      key: 'contacts_access_to_mktp',
      operator: 'greater_than_or_equals',
      value: contactFilter?.contacts_access_to_mktp_min,
    });
  if (contactFilter?.contacts_access_to_mktp_max)
    filters.push({
      key: 'contacts_access_to_mktp',
      operator: 'less_than_or_equals',
      value: contactFilter?.contacts_access_to_mktp_max,
    });

  const organizeFilter = filter?.organizeFilter;
  if (organizeFilter?.nbr_catalogs_min)
    filters.push({
      key: 'nbr_catalogs',
      operator: 'greater_than_or_equals',
      value: organizeFilter?.nbr_catalogs_min,
    });
  if (organizeFilter?.nbr_catalogs_max)
    filters.push({
      key: 'nbr_catalogs',
      operator: 'less_than_or_equals',
      value: organizeFilter?.nbr_catalogs_max,
    });
  if (organizeFilter?.custom_discount_min)
    filters.push({
      key: 'custom_discount',
      operator: 'greater_than_or_equals',
      value: organizeFilter?.custom_discount_min,
    });
  if (organizeFilter?.custom_discount_max)
    filters.push({
      key: 'custom_discount',
      operator: 'less_than_or_equals',
      value: organizeFilter?.custom_discount_max,
    });
  if (organizeFilter?.fav_categories.length > 0)
    filters.push({
      key: 'fav_categories',
      operator: 'in',
      value: organizeFilter?.fav_categories.map((category) => category.id),
    });
  if (organizeFilter?.fav_brands.length > 0)
    filters.push({
      key: 'fav_brands',
      operator: 'in',
      value: organizeFilter?.fav_brands?.map((brand) => brand.id),
    });

  if (organizeFilter?.groups.length > 0)
    filters.push({
      key: 'groups',
      operator: 'in',
      value: organizeFilter?.groups?.map((group) => group.id),
    });

  return { filters };
};
export const refillCompanyFilter = (
  filter?: ICompanyFilter
): ICompanyFilter => {
  const refillFilter: ICompanyFilter = {
    companiesInfoFilter: {
      deleted: filter?.companiesInfoFilter?.deleted ?? 'false',
      name: filter?.companiesInfoFilter?.name,
      nbr_locations_min: filter?.companiesInfoFilter?.nbr_locations_min,
      nbr_locations_max: filter?.companiesInfoFilter?.nbr_locations_max,
      total_sales_min: filter?.companiesInfoFilter?.total_sales_min,
      total_sales_max: filter?.companiesInfoFilter?.total_sales_max,
      total_orders_min: filter?.companiesInfoFilter?.total_orders_min,
      total_orders_max: filter?.companiesInfoFilter?.total_orders_max,
      active_from: filter?.companiesInfoFilter?.active_from,
      active_to: filter?.companiesInfoFilter?.active_to,
    },
    locationInfoFilter: {
      location_name: filter?.locationInfoFilter?.location_name,
      has_billing: filter?.locationInfoFilter?.has_billing != null ? !!filter?.locationInfoFilter?.has_billing :  undefined,
      shipping_term: filter?.locationInfoFilter?.shipping_term != null ? !!filter?.locationInfoFilter?.shipping_term : undefined,
      payment_terms: filter?.locationInfoFilter?.payment_terms || [],
    },
    contactFilter: {
      contact_name: filter?.contactFilter?.contact_name,
      nbr_contacts_min: filter?.contactFilter?.nbr_contacts_min,
      nbr_contacts_max: filter?.contactFilter?.nbr_contacts_max,
      contacts_access_to_mktp_min:
        filter?.contactFilter?.contacts_access_to_mktp_min,
      contacts_access_to_mktp_max:
        filter?.contactFilter?.contacts_access_to_mktp_max,
    },
    organizeFilter: {
      nbr_catalogs_min: filter?.organizeFilter?.nbr_catalogs_min,
      nbr_catalogs_max: filter?.organizeFilter?.nbr_catalogs_max,
      custom_discount_min: filter?.organizeFilter?.custom_discount_min,
      custom_discount_max: filter?.organizeFilter?.custom_discount_max,
      fav_categories: filter?.organizeFilter?.fav_categories || [],
      fav_brands: filter?.organizeFilter?.fav_brands || [],
      groups: filter?.organizeFilter?.groups || [],
    },
  };
  return JSON.parse(JSON.stringify(refillFilter));
};

export const parseOrdersFilterRequest = (
  filter: IOrdersFilter
): IOrderFilterRequest => {
  const filters: { key: string; operator: string; value: any }[] = [];
  const generalInfoFilter = filter?.generalInfoFilter;
  if (generalInfoFilter?.name !== undefined)
    filters.push({
      key: 'name',
      operator: 'contains',
      value: generalInfoFilter?.name,
    });
  if (generalInfoFilter?.id_order !== undefined)
    filters.push({
      key: 'id_order',
      operator: 'contains',
      value: generalInfoFilter?.id_order,
    });
  if (generalInfoFilter?.order_status !== undefined)
    filters.push({
      key: 'order_status',
      operator: 'equals',
      value: generalInfoFilter?.order_status,
    });
  if (generalInfoFilter?.payment_status !== undefined)
    filters.push({
      key: 'payment_status',
      operator: 'equals',
      value: generalInfoFilter?.payment_status,
    });
  if (generalInfoFilter?.catalogs?.length > 0)
    filters.push({
      key: 'pricelist',
      operator: 'in',
      value: generalInfoFilter?.catalogs.map((catalog) => catalog.id),
    });
  const own = filter?.ownershipFilter;
  if (own?.my_orders === true) {
    filters.push({ key: 'my_orders', operator: 'equals', value: true });
  }
  const ownerId = own?.owner?.id;
  if (ownerId !== undefined && ownerId !== null) {
    filters.push({
      key: 'owner_id',
      operator: 'equals',
      value: ownerId,
    });
  }
  const creatorId = own?.creator?.id;
  if (creatorId !== undefined && creatorId !== null) {
    filters.push({
      key: 'created_by_id',
      operator: 'equals',
      value: creatorId,
    });
  }
  const advancedInfoFilter = filter?.advancedFilter;
  if (advancedInfoFilter?.total_SKU_min !== undefined)
    filters.push({
      key: 'nbr_products',
      operator: 'greater_than_or_equals',
      value: advancedInfoFilter?.total_SKU_min,
    });
  if (advancedInfoFilter?.total_SKU_max !== undefined)
    filters.push({
      key: 'nbr_products',
      operator: 'less_than_or_equals',
      value: advancedInfoFilter?.total_SKU_max,
    });
  if (advancedInfoFilter?.total_Qty_min !== undefined)
    filters.push({
      key: 'quantity',
      operator: 'greater_than_or_equals',
      value: advancedInfoFilter?.total_Qty_min,
    });
  if (advancedInfoFilter?.total_Qty_max !== undefined)
    filters.push({
      key: 'quantity',
      operator: 'less_than_or_equals',
      value: advancedInfoFilter?.total_Qty_max,
    });
  if (advancedInfoFilter?.total_price_min !== undefined)
    filters.push({
      key: 'amount_total',
      operator: 'greater_than_or_equals',
      value: advancedInfoFilter?.total_price_min,
    });
  if (advancedInfoFilter?.total_price_max !== undefined)
    filters.push({
      key: 'amount_total',
      operator: 'less_than_or_equals',
      value: advancedInfoFilter?.total_price_max,
    });
  if (advancedInfoFilter?.est_margin_min !== undefined)
    filters.push({
      key: 'est_margin',
      operator: 'greater_than_or_equals',
      value: advancedInfoFilter?.est_margin_min,
    });
  if (advancedInfoFilter?.est_margin_max !== undefined)
    filters.push({
      key: 'est_margin',
      operator: 'less_than_or_equals',
      value: advancedInfoFilter?.est_margin_max,
    });
  if (advancedInfoFilter?.date_order_min !== undefined)
    filters.push({
      key: 'date_order',
      operator: 'greater_than_or_equals',
      value: advancedInfoFilter?.date_order_min,
    });
  if (advancedInfoFilter?.date_order_max !== undefined)
    filters.push({
      key: 'date_order',
      operator: 'less_than_or_equals',
      value: advancedInfoFilter?.date_order_max,
    });
  return { filters };
};
export const refillOrdersFilter = (filter?: IOrdersFilter): IOrdersFilter => {
  const ownLegacy = filter?.ownershipFilter as
    | IOrdersFilter['ownershipFilter']
    | { owner_id?: number; created_by_id?: number }
    | undefined;
  let owner = filter?.ownershipFilter?.owner;
  let creator = filter?.ownershipFilter?.creator;
  if (!owner && ownLegacy && 'owner_id' in ownLegacy && ownLegacy.owner_id != null) {
    owner = { id: ownLegacy.owner_id, name: '', email: undefined };
  }
  if (!creator && ownLegacy && 'created_by_id' in ownLegacy && ownLegacy.created_by_id != null) {
    creator = { id: ownLegacy.created_by_id, name: '', email: undefined };
  }
  const refillFilter: IOrdersFilter = {
    generalInfoFilter: {
      name: filter?.generalInfoFilter?.name,
      id_order: filter?.generalInfoFilter?.id_order,
      order_status: filter?.generalInfoFilter?.order_status,
      payment_status: filter?.generalInfoFilter?.payment_status,
      catalogs: filter?.generalInfoFilter?.catalogs || [],
    },
    ownershipFilter: {
      my_orders: filter?.ownershipFilter?.my_orders,
      owner: owner ?? undefined,
      creator: creator ?? undefined,
    },
    advancedFilter: {
      total_SKU_min: filter?.advancedFilter?.total_SKU_min,
      total_SKU_max: filter?.advancedFilter?.total_SKU_max,
      total_Qty_min: filter?.advancedFilter?.total_Qty_min,
      total_Qty_max: filter?.advancedFilter?.total_Qty_max,
      total_price_min: filter?.advancedFilter?.total_price_min,
      total_price_max: filter?.advancedFilter?.total_price_max,
      est_margin_min: filter?.advancedFilter?.est_margin_min,
      est_margin_max: filter?.advancedFilter?.est_margin_max,
      date_order_min: filter?.advancedFilter?.date_order_min,
      date_order_max: filter?.advancedFilter?.date_order_max,
    },
  };
  return JSON.parse(JSON.stringify(refillFilter));
};

export const parseCatalogsFilterRequest = (
  filter: ICatalogFilter
): ICatalogFilterRequest => {
  const filters: { key: string; operator: string; value: any }[] = [];

  const basicInfoFilter = filter?.basicInfoFilter;
  if (basicInfoFilter?.name)
    filters.push({
      key: 'name',
      operator: 'contains',
      value: basicInfoFilter?.name,
    });
  if (basicInfoFilter?.status !== undefined)
    filters.push({
      key: 'status',
      operator: 'equals',
      value: basicInfoFilter?.status,
    });
  if (basicInfoFilter?.type !== undefined)
    filters.push({
      key: 'type',
      operator: 'equals',
      value: basicInfoFilter?.type,
    });
  if (basicInfoFilter?.start_date_min !== undefined)
    filters.push({
      key: 'start_date',
      operator: 'greater_than_or_equals',
      value: basicInfoFilter?.start_date_min,
    });
  if (basicInfoFilter?.start_date_max !== undefined)
    filters.push({
      key: 'start_date',
      operator: 'less_than_or_equals',
      value: basicInfoFilter?.start_date_max,
    });
  if (basicInfoFilter?.end_date_min !== undefined && isValidDate(basicInfoFilter?.end_date_min))
    filters.push({
      key: 'end_date',
      operator: 'greater_than_or_equals',
      value: basicInfoFilter?.end_date_min,
    });
  if (basicInfoFilter?.end_date_max !== undefined && isValidDate(basicInfoFilter?.end_date_max))
    filters.push({
      key: 'end_date',
      operator: 'less_than_or_equals',
      value: basicInfoFilter?.end_date_max,
    });
  const rulesFilter = filter?.rulesFilter;
  if (rulesFilter?.increment_min !== undefined)
    filters.push({
      key: 'increment',
      operator: 'greater_than_or_equals',
      value: rulesFilter?.increment_min,
    });
  if (rulesFilter?.increment_max !== undefined)
    filters.push({
      key: 'increment',
      operator: 'less_than_or_equals',
      value: rulesFilter?.increment_max,
    });
    if (rulesFilter?.decrement_min !== undefined)
    filters.push({
      key: 'decrement',
      operator: 'greater_than_or_equals',
      value: rulesFilter?.decrement_min,
    });
  if (rulesFilter?.decrement_max !== undefined)
    filters.push({
      key: 'decrement',
      operator: 'less_than_or_equals',
      value: rulesFilter?.decrement_max,
    });
  if (rulesFilter?.min_quantity !== undefined)
    filters.push({
      key: 'min_quantity',
      operator: 'greater_than_or_equals',
      value: rulesFilter?.min_quantity,
    });
  if (rulesFilter?.max_quantity !== undefined)
    filters.push({
      key: 'max_quantity',
      operator: 'less_than_or_equals',
      value: rulesFilter?.max_quantity,
    });
  const productsFilter = filter?.productsFilter;
  if (productsFilter?.product_name)
    filters.push({
      key: 'product_name',
      operator: 'contains',
      value: productsFilter?.product_name,
    });
  if (productsFilter?.nbr_products_min !== undefined)
    filters.push({
      key: 'nbr_products',
      operator: 'greater_than_or_equals',
      value: productsFilter?.nbr_products_min,
    });
  if (productsFilter?.nbr_products_max !== undefined)
    filters.push({
      key: 'nbr_products',
      operator: 'less_than_or_equals',
      value: productsFilter?.nbr_products_max,
    });
  if (productsFilter?.nbr_products_overriding_general_rule_min !== undefined)
    filters.push({
      key: 'nbr_products_overriding_general_rule',
      operator: 'greater_than_or_equals',
      value: productsFilter?.nbr_products_overriding_general_rule_min,
    });
  if (productsFilter?.nbr_products_overriding_general_rule_max !== undefined)
    filters.push({
      key: 'nbr_products_overriding_general_rule',
      operator: 'less_than_or_equals',
      value: productsFilter?.nbr_products_overriding_general_rule_max,
    });
  if (productsFilter?.brands?.length > 0)
    filters.push({
      key: 'brand',
      operator: 'in',
      value: productsFilter?.brands.map((brand) => brand.id),
    });
  if (productsFilter?.categories?.length > 0)
    filters.push({
      key: 'category',
      operator: 'in',
      value: productsFilter?.categories.map((category) => category.id),
    });
  if (productsFilter?.tags?.length > 0)
    filters.push({
      key: 'tag',
      operator: 'in',
      value: productsFilter?.tags.map((tag) => tag.id),
    });
  const locationsFilter = filter?.locationsFilter;
  if (locationsFilter?.locations?.length > 0)
    filters.push({
      key: 'location_name',
      operator: 'in',
      value: locationsFilter?.locations.map((location) => location.id),
    });
  if (locationsFilter?.client_groups?.length > 0)
    filters.push({
      key: 'client_group',
      operator: 'in',
      value: locationsFilter?.client_groups.map(
        (client_group) => client_group.id
      ),
    });
  if (locationsFilter?.nbr_locations_min !== undefined)
    filters.push({
      key: 'nbr_locations',
      operator: 'greater_than_or_equals',
      value: locationsFilter?.nbr_locations_min,
    });
  if (locationsFilter?.nbr_locations_max !== undefined)
    filters.push({
      key: 'nbr_locations',
      operator: 'less_than_or_equals',
      value: locationsFilter?.nbr_locations_max,
    });

  return { filters };
};

export const refillCatalogsFilter = (
  filter?: ICatalogFilter
): ICatalogFilter => {
  const refillFilter: ICatalogFilter = {
    basicInfoFilter: {
      name: filter?.basicInfoFilter?.name,
      status: filter?.basicInfoFilter?.status,
      type: filter?.basicInfoFilter?.type,
      start_date_min: isValidDate(filter?.basicInfoFilter?.start_date_min) ? filter?.basicInfoFilter?.start_date_min : undefined,
      start_date_max: isValidDate(filter?.basicInfoFilter?.start_date_max) ? filter?.basicInfoFilter?.start_date_max : undefined,
      end_date_min: isValidDate(filter?.basicInfoFilter?.end_date_min) ? filter?.basicInfoFilter?.end_date_min : undefined,
      end_date_max: isValidDate(filter?.basicInfoFilter?.end_date_max) ? filter?.basicInfoFilter?.end_date_max : undefined,
    },
    rulesFilter: {
      increment_min: filter?.rulesFilter?.increment_min,
      increment_max: filter?.rulesFilter?.increment_max,
      decrement_min: filter?.rulesFilter?.decrement_min,
      decrement_max: filter?.rulesFilter?.decrement_max,
      min_quantity: filter?.rulesFilter?.min_quantity,
      max_quantity: filter?.rulesFilter?.max_quantity,
    },
    productsFilter: {
      product_name: filter?.productsFilter?.product_name,
      nbr_products_min: filter?.productsFilter?.nbr_products_min,
      nbr_products_max: filter?.productsFilter?.nbr_products_max,
      nbr_products_overriding_general_rule_min:
        filter?.productsFilter?.nbr_products_overriding_general_rule_min,
      nbr_products_overriding_general_rule_max:
        filter?.productsFilter?.nbr_products_overriding_general_rule_max,
      brands: filter?.productsFilter?.brands || [],
      categories: filter?.productsFilter?.categories || [],
      tags: filter?.productsFilter?.tags || [],
    },
    locationsFilter: {
      locations: filter?.locationsFilter?.locations || [],
      client_groups: filter?.locationsFilter?.client_groups || [],
      nbr_locations_min: filter?.locationsFilter?.nbr_locations_min,
      nbr_locations_max: filter?.locationsFilter?.nbr_locations_max,
    },
  };
  return JSON.parse(JSON.stringify(refillFilter));
};
export const parseSegmentFilterRequest = (
  filter: ISegmentFilter
): ISegmentFilterRequest => {
  const filters: { key: string; operator: string; value: any }[] = [];

  const segmentInfoFilter = filter?.segmentsInfoFilter;
  if (segmentInfoFilter?.name !== undefined)
    filters.push({
      key: 'name',
      operator: 'contains',
      value: segmentInfoFilter?.name,
    });
  if (segmentInfoFilter?.status !== undefined)
    filters.push({
      key: 'status',
      operator: 'equals',
      value: segmentInfoFilter?.status,
    });
  if (segmentInfoFilter?.value_type !== undefined)
    filters.push({
      key: 'value_type',
      operator: 'equals',
      value: segmentInfoFilter?.value_type,
    });

  const advancedFilter = filter?.advancedFilter;
  if (advancedFilter?.nbr_locations_min !== undefined)
    filters.push({
      key: 'nbr_locations',
      operator: 'greater_than_or_equals',
      value: advancedFilter?.nbr_locations_min,
    });
  if (advancedFilter?.nbr_locations_max !== undefined)
    filters.push({
      key: 'nbr_locations',
      operator: 'less_than_or_equals',
      value: advancedFilter?.nbr_locations_max,
    });
  if (advancedFilter?.locations?.length > 0)
    filters.push({
      key: 'locations',
      operator: 'in',
      value: advancedFilter?.locations.map((location) => location.id),
    });
  return { filters };
};

export const refillSegmentFilter = (
  filter?: ISegmentFilter
): ISegmentFilter => {
  const refillFilter: ISegmentFilter = {
    segmentsInfoFilter: {
      name: filter?.segmentsInfoFilter?.name,
      status: filter?.segmentsInfoFilter?.status,
      value_type: filter?.segmentsInfoFilter?.value_type,
    },
    advancedFilter: {
      nbr_locations_min: filter?.advancedFilter?.nbr_locations_min,
      nbr_locations_max: filter?.advancedFilter?.nbr_locations_max,
      locations: filter?.advancedFilter?.locations || [],
    },
  };
  return JSON.parse(JSON.stringify(refillFilter));
};

export const parseGroupFilterRequest = (
  filter: IGroupFilter
): IGroupFilterRequest => {
  const filters: { key: string; operator: string; value: any }[] = [];

  const groupInfoFilter = filter?.groupInfoFilter;
  if (groupInfoFilter?.name)
    filters.push({
      key: 'name',
      operator: 'contains',
      value: groupInfoFilter?.name,
    });
  if (groupInfoFilter?.nbr_locations_min !== undefined)
    filters.push({
      key: 'nbr_locations',
      operator: 'greater_than_or_equals',
      value: groupInfoFilter?.nbr_locations_min,
    });
  if (groupInfoFilter?.nbr_locations_max !== undefined)
    filters.push({
      key: 'nbr_locations',
      operator: 'less_than_or_equals',
      value: groupInfoFilter?.nbr_locations_max,
    });

  if (groupInfoFilter?.locations.length > 0)
    filters.push({
      key: 'locations',
      operator: 'in',
      value: groupInfoFilter?.locations.map((group) => group.id),
    });

  const advancedFilter = filter?.advancedFilter;

  if (advancedFilter?.total_orders_amount_min !== undefined)
    filters.push({
      key: 'total_orders_amount',
      operator: 'greater_than_or_equals',
      value: advancedFilter?.total_orders_amount_min,
    });
  if (advancedFilter?.total_orders_amount_max !== undefined)
    filters.push({
      key: 'total_orders_amount',
      operator: 'less_than_or_equals',
      value: advancedFilter?.total_orders_amount_max,
    });
  if (advancedFilter?.total_orders_count_min !== undefined)
    filters.push({
      key: 'total_orders_count',
      operator: 'greater_than_or_equals',
      value: advancedFilter?.total_orders_count_min,
    });
  if (advancedFilter?.total_orders_count_max !== undefined)
    filters.push({
      key: 'total_orders_count',
      operator: 'less_than_or_equals',
      value: advancedFilter?.total_orders_count_max,
    });
  return { filters };
};

export const refillGroupFilter = (filter?: IGroupFilter): IGroupFilter => {
  const refillFilter: IGroupFilter = {
    groupInfoFilter: {
      name: filter?.groupInfoFilter?.name,
      nbr_locations_min: filter?.groupInfoFilter?.nbr_locations_min,
      nbr_locations_max: filter?.groupInfoFilter?.nbr_locations_max,
      locations: filter?.groupInfoFilter?.locations || [],
    },
    advancedFilter: {
      total_orders_amount_min: filter?.advancedFilter?.total_orders_amount_min,
      total_orders_amount_max: filter?.advancedFilter?.total_orders_amount_min,
      total_orders_count_min: filter?.advancedFilter?.total_orders_count_min,
      total_orders_count_max: filter?.advancedFilter?.total_orders_count_min,
    },
  };
  return JSON.parse(JSON.stringify(refillFilter));
};
export const parseImportFilterRequest = (
  filter: IImportFilter
): IImportFilterRequest => {
  const filters: { key: string; operator: string; value: any }[] = [];

  const importInfoFilter = filter?.importInfofilter;
  if (importInfoFilter?.name !== undefined)
    filters.push({
      key: 'name',
      operator: 'contains',
      value: importInfoFilter?.name,
    });
  if (importInfoFilter?.start_date !== undefined)
    filters.push({
      key: 'date',
      operator: 'greater_than_or_equals',
      value: importInfoFilter?.start_date,
    });
  if (importInfoFilter?.end_date !== undefined)
    filters.push({
      key: 'date',
      operator: 'less_than_or_equals',
      value: importInfoFilter?.end_date,
    });

  if (importInfoFilter?.status !== undefined)
    filters.push({
      key: 'status',
      operator: 'equals',
      value: (importInfoFilter?.status === "imported" ? 1 : 0),
    });
  if (importInfoFilter?.nbr_products_min !== undefined)
    filters.push({
      key: 'nbr_products',
      operator: 'greater_than_or_equals',
      value: importInfoFilter?.nbr_products_min,
    });
  if (importInfoFilter?.nbr_products_max !== undefined)
    filters.push({
      key: 'nbr_products',
      operator: 'less_than_or_equals',
      value: importInfoFilter?.nbr_products_max,
    });

  return { filters };
};

export const refillImportsFilter = (filter?: IImportFilter): IImportFilter => {
  const refillFilter: IImportFilter = {
    importInfofilter: {
      name: filter?.importInfofilter?.name,
      start_date: filter?.importInfofilter?.start_date,
      end_date: filter?.importInfofilter?.end_date,
      status: filter?.importInfofilter?.status,
      nbr_products_min: filter?.importInfofilter?.nbr_products_min,
      nbr_products_max: filter?.importInfofilter?.nbr_products_max,
    },
  };
  return JSON.parse(JSON.stringify(refillFilter));
};

export const parseAgentFilterRequest = (
  filter: IAgentFilter
): IAgentFilterRequest => {
  const filters: { key: string; operator: string; value: any }[] = [];

  const agentsInfoFilter = filter?.agentsInfoFilter;
  if (agentsInfoFilter?.first_name)
    filters.push({
      key: 'first_name',
      operator: 'contains',
      value: agentsInfoFilter?.first_name,
    });
  if (agentsInfoFilter?.last_name)
    filters.push({
      key: 'last_name',
      operator: 'contains',
      value: agentsInfoFilter?.last_name,
    });
  if (agentsInfoFilter?.email)
    filters.push({
      key: 'email',
      operator: 'contains',
      value: agentsInfoFilter?.email,
    });
    if (agentsInfoFilter?.phone != null)
    filters.push({
      key: 'phone',
      operator: 'contains',
      value: agentsInfoFilter?.phone,
    })
  if (agentsInfoFilter?.country !== undefined)
    filters.push({
      key: 'country',
      operator: 'equals',
      value: agentsInfoFilter?.country,
    });
  if (agentsInfoFilter?.address !== undefined)
    filters.push({
      key: 'address',
      operator: 'equals',
      value: agentsInfoFilter?.address,
    });
  if (agentsInfoFilter?.vat !== undefined)
    filters.push({
      key: 'vat',
      operator: 'equals',
      value: agentsInfoFilter?.vat,
    });
  if (agentsInfoFilter?.billing_address !== undefined)
    filters.push({
      key: 'billing_address',
      operator: 'contains',
      value: agentsInfoFilter?.billing_address,
    });

  const extraInfoFilter = filter?.extraInfoFilter;
  if (extraInfoFilter?.catalog_name !== undefined)
    filters.push({
      key: 'catalog_name',
      operator: 'contains',
      value: extraInfoFilter?.catalog_name,
    });
  if (extraInfoFilter?.tags?.length > 0)
    filters.push({
      key: 'tag_name',
      operator: 'in',
      value: extraInfoFilter?.tags,
    });
  if (extraInfoFilter?.location_name !== undefined)
    filters.push({
      key: 'location_name',
      operator: 'contains',
      value: extraInfoFilter?.location_name,
    });
  if (extraInfoFilter?.nbr_orders_min !== undefined)
    filters.push({
      key: 'nbr_orders',
      operator: 'greater_than_or_equals',
      value: extraInfoFilter?.nbr_orders_min,
    });
  if (extraInfoFilter?.nbr_orders_max !== undefined)
    filters.push({
      key: 'nbr_orders',
      operator: 'less_than_or_equals',
      value: extraInfoFilter?.nbr_orders_max,
    });
  if (extraInfoFilter?.nbr_locations_min !== undefined)
    filters.push({
      key: 'nbr_locations',
      operator: 'greater_than_or_equals',
      value: extraInfoFilter?.nbr_locations_min,
    });
  if (extraInfoFilter?.nbr_locations_max !== undefined)
    filters.push({
      key: 'nbr_locations',
      operator: 'less_than_or_equals',
      value: extraInfoFilter?.nbr_locations_max,
    });
  if (extraInfoFilter?.nbr_catalogs_min !== undefined)
    filters.push({
      key: 'nbr_catalogs',
      operator: 'greater_than_or_equals',
      value: extraInfoFilter?.nbr_catalogs_min,
    });
  if (extraInfoFilter?.nbr_catalogs_max !== undefined)
    filters.push({
      key: 'nbr_catalogs',
      operator: 'less_than_or_equals',
      value: extraInfoFilter?.nbr_catalogs_max,
    });
  if (extraInfoFilter?.has_base_commission !== undefined) {
    const hasBase = extraInfoFilter.has_base_commission;
    const isTrue = hasBase === true || (typeof hasBase === 'string' && hasBase.toLowerCase() === 'true');

    filters.push({
      key: 'has_base_commission',
      operator: 'equals',
      value: isTrue,
    });
  }


  return { filters };
};

export const refillAgentFilter = (
  filter?: IAgentFilter
): IAgentFilter => {
  const refillFilter: IAgentFilter = {
    agentsInfoFilter: {
      first_name: filter?.agentsInfoFilter?.first_name,
      last_name: filter?.agentsInfoFilter?.last_name,
      email: filter?.agentsInfoFilter?.email,
      phone: filter?.agentsInfoFilter?.phone,
      country: filter?.agentsInfoFilter?.country,
      address: filter?.agentsInfoFilter?.address,
      vat: filter?.agentsInfoFilter?.vat,
      billing_address: filter?.agentsInfoFilter?.billing_address,
    },
    extraInfoFilter: {
      catalog_name: filter?.extraInfoFilter?.catalog_name,
      tags: filter?.extraInfoFilter?.tags ?? [],
      location_name: filter?.extraInfoFilter?.location_name,
      nbr_orders_min: filter?.extraInfoFilter?.nbr_orders_min,
      nbr_orders_max: filter?.extraInfoFilter?.nbr_orders_max,
      nbr_locations_min: filter?.extraInfoFilter?.nbr_locations_min,
      nbr_locations_max: filter?.extraInfoFilter?.nbr_locations_max,
      nbr_catalogs_min: filter?.extraInfoFilter?.nbr_catalogs_min,
      nbr_catalogs_max: filter?.extraInfoFilter?.nbr_catalogs_max,
      has_base_commission: filter?.extraInfoFilter?.has_base_commission,
    },
  };
  return JSON.parse(JSON.stringify(refillFilter));
};

export const parseLocationToItemData = (location: ILocation): ItemData => {
  return {
    id: location.id,
    label: location.name,
    company_name: location.company_name,
    add_discount: location.custom_discount,
    description: location.apartment ? location.apartment + '. ' : '',
    type: 'location',
  };
};
/**
 * Format time in seconds to a human-readable string in the format of yd hm s
 * @param timeInSeconds the time in seconds
 * @returns a string like "1y 2d 3h 4m 5s" or an empty string if the input is undefined
 */
export const formatResponseTime = (timeInSeconds: number): string => {
  if (timeInSeconds === undefined || timeInSeconds === null || timeInSeconds <= 0) return 'NA';
  const years = Math.floor(timeInSeconds / (60 * 60 * 24 * 365));
  const days = Math.floor(
    (timeInSeconds % (60 * 60 * 24 * 365)) / (60 * 60 * 24)
  );
  const hours = Math.floor((timeInSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((timeInSeconds % (60 * 60)) / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  let result = '';
  if (years > 0) result += `${years}y `;
  if (days > 0) result += `${days}d `;
  if (hours > 0) result += `${hours}h `;
  if (minutes > 0) result += `${minutes}m `;
  if (seconds > 0) result += `${seconds}s`;
  return result.trim();
};

/**
 * Return the translation of the payment status of an order. If the status
 * is not recognized, return the original status.
 * @param status the status of the order, one of
 * 'not paid', 'in payment', 'paid', 'partial', 'reversed'
 * @returns the translation of the status, or the original status if not recognized
 */
export const getOrderPaymentStatus = (status: string | undefined): string => {
  return (
    OrderPaymentStatusEnum[status as keyof typeof OrderPaymentStatusEnum] ||
    status
  );
};

/**
 * Return the internal representation of the payment status of an order.
 * The internal representation is the same as the translation of the status,
 * but with all spaces replaced by underscores.
 * @param status the status of the order, one of
 * 'not paid', 'in payment', 'paid', 'partial', 'reversed'
 * @returns the internal representation of the status
 */
export const getInternalPaymentStatus = (status: string): string => {
  return status.toLowerCase().replace(' ', '_');
};

export const capitalize = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const initNavbarParams = (
  store: Store<AppState>,
  activatedRoute: ActivatedRoute | null,
  reset: boolean = false
) => {
  const excluded_modules = ['catalogs'];
  let navigationData = {
    title: null,
    breadcrumbs: [] as { label: string; url: string | null }[],
    parentPath: null,
    icon: null,
  };

  if (!reset && activatedRoute) {
    const module = activatedRoute.snapshot?.data['module'] ?? '';
    // Ensure breadcrumbs is always an array to prevent .map() errors
    const breadcrumbs = Array.isArray(activatedRoute.snapshot?.data['breadcrumbs']) 
      ? activatedRoute.snapshot.data['breadcrumbs'] 
      : [];
    const parentPath = activatedRoute.snapshot?.data['parentPath'] ?? null;
    const icon = activatedRoute.snapshot?.data['icon'] ?? '';
    let _breadcrumbs: { label: string; url: string | null }[] = [];
    activatedRoute.params.subscribe((params) => {
      if (Object.keys(params).length !== 0) {
        _breadcrumbs = breadcrumbs.map(
          (breadcrumb: { label: string; url: string }) => ({
            ...breadcrumb,
            url: Object.entries(params).reduce(
              (updatedUrl, [key, value]) =>
                updatedUrl?.replaceAll(`{${key}}`, String(value)),
              breadcrumb.url
            ),
          })
        );
      } else {
        _breadcrumbs = breadcrumbs;
      }
    });
    navigationData = {
      title: module,
      breadcrumbs: excluded_modules.includes(module) ? [] : _breadcrumbs,
      parentPath: parentPath,
      icon: icon,
    };
  }
  store.dispatch(SetNavbarParams(navigationData));
};

export function refillFilter(module: string | null, filter?: any): IInventoryFilter | ICompanyFilter | IQuotationFilter | IOrdersFilter | IImportFilter | ICatalogFilter | IGroupFilter | ISegmentFilter | IAgentFilter | null {
  switch (module) {
    case 'inventory':
      return refillInventoryFilter(filter);
    case 'company':
      return refillCompanyFilter(filter);
    case 'quotation':
      return refillQuotationFilter(filter);
    case 'order':
      return refillOrdersFilter(filter);
    case 'import':
      return refillImportsFilter(filter);
    case 'catalog':
      return refillCatalogsFilter(filter);
    case 'companyGroup':
      return refillGroupFilter(filter);
    case 'segment':
      return refillSegmentFilter(filter);
    case 'agent':
      return refillAgentFilter(filter);
    default:
      return null;
  }
}

export function parseFilterRequest(module:string | null, filter: IInventoryFilter | ICompanyFilter | IQuotationFilter | IOrdersFilter | IImportFilter | ICatalogFilter | IGroupFilter | ISegmentFilter | IAgentFilter): IInventoryFilterRequest | ICompanyFilterRequest | IQuotationFilterRequest | IOrderFilterRequest | IImportFilterRequest | ICatalogFilterRequest | IGroupFilterRequest | ISegmentFilterRequest | IAgentFilterRequest | undefined {
  switch (module) {
    case 'inventory':
      return parseInventoryFilterRequest(filter as IInventoryFilter)
    case 'company':
      return parseCompanyFilterRequest(filter as ICompanyFilter)
    case 'quotation':
      return parseQuotationFilterRequest(filter as IQuotationFilter)
    case 'order':
      return parseOrdersFilterRequest(filter as IOrdersFilter)
    case 'import':
      return parseImportFilterRequest(filter as IImportFilter)
    case 'catalog':
      return parseCatalogsFilterRequest(filter as ICatalogFilter)
    case 'companyGroup':
      return parseGroupFilterRequest(filter as IGroupFilter)
    case 'segment':
      return parseSegmentFilterRequest(filter as ISegmentFilter)
    case 'agent':
      return parseAgentFilterRequest(filter as IAgentFilter)
    default:
      return undefined
  }
}

export const removeEmptyProperties = (obj: any, removeBoolFalse = true): any => {
  if (Array.isArray(obj)) {
    const cleanedArray = obj
      .map(item => removeEmptyProperties(item, removeBoolFalse))
      .filter(item => item !== undefined);
    return cleanedArray.length > 0 ? cleanedArray : undefined;
  } else if (obj && typeof obj === 'object') {
    const newObj: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = removeEmptyProperties(obj[key], removeBoolFalse);
        const shouldKeep =
          value !== undefined &&
          (removeBoolFalse ? value !== false : true);

        if (shouldKeep) {
          newObj[key] = value;
        }
      }
    }
    return Object.keys(newObj).length > 0 ? newObj : undefined;
  } else if (removeBoolFalse === true) {
    return obj === false ? undefined : obj;
  } else {
    return obj;
  }
};



export const fillMissingFields = (source: any, template: any): any => {
  if (Array.isArray(template)) {
    if (Array.isArray(source)) {
      return source.map((item, index) =>
        fillMissingFields(item, template[0] || {})
      );
    }
    return [];
  } else if (template && typeof template === 'object') {
    const result: any = {};
    for (const key in template) {
      if (template.hasOwnProperty(key)) {
        if (source && source.hasOwnProperty(key)) {
          result[key] = fillMissingFields(source[key], template[key]);
        } else {
          result[key] = template[key];
        }
      }
    }
    return result;
  }
  return source !== undefined ? source : template;
}
export const deepConvertBooleans = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(item => deepConvertBooleans(item));
  } else if (typeof obj === 'object' && obj !== null) {
    const result: any = {};
    for (const key in obj) {
      result[key] = deepConvertBooleans(obj[key]);
    }
    return result;
  } else if (typeof obj === 'boolean') {
    return obj ? 1 : 0;
  } else {
    return obj;
  }
}
export const encodeFilterObject = (filterObject:any, removeBoolFalse = true):string | null => {
  if(filterObject){
    const filter = deepConvertBooleans(removeEmptyProperties(filterObject, removeBoolFalse));
    if(filter){
      const encodedFilter =  encodeURIComponent(JSON.stringify(filter));
      return encodedFilter
    }
  }
  return null;
}

export const isValidDate = (dateString: string | undefined): boolean => {
  if(dateString == undefined)
    return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

/**
 * Returns the user's timezone offset from UTC in hours.
 * A positive value indicates ahead of UTC, negative means behind.
 * Example: For UTC+2, returns 2.
 */
export const getUserUtcOffset = (): number => {
  const utcOffset = new Date().getTimezoneOffset();
  return -1 * (utcOffset ?? 0) / 60;
};

/**
 * Parses a datetime the way Odoo serializes one: naive UTC, with no timezone
 * marker. `new Date()` reads that as local time, so any arithmetic on it is
 * wrong by the viewer's offset — east of UTC a 60s throttle reads as hours,
 * west of it as already expired.
 *
 * For display, prefer the `adaptToUserUtcOffset` pipe. This is for arithmetic.
 */
export const parseOdooDateTime = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);
  const isoish = value.includes('T') ? value : value.replace(' ', 'T');
  const parsed = new Date(hasZone ? isoish : `${isoish}Z`);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/** Whole seconds until `value`, 0 once it has passed or cannot be parsed. */
export const secondsUntilOdooDateTime = (value?: string | null): number => {
  const target = parseOdooDateTime(value);
  if (!target) {
    return 0;
  }
  const remaining = (target.getTime() - Date.now()) / 1000;
  return remaining > 0 ? Math.ceil(remaining) : 0;
};
